import React, { useState, useCallback, useRef } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Avatar,
  Stack,
  InputAdornment,
  Popover,
  ClickAwayListener,
  useTheme,
} from '@mui/material';
import { Send, EmojiEmotions } from '@mui/icons-material';
import EmojiPicker from 'emoji-picker-react';
import { useAppDispatch, useAppSelector, createComment } from '@/store';
import { useNavigate } from 'react-router-dom';

interface CommentFormProps {
  feedId: string;
  parentCommentId?: string | null;
  placeholder?: string;
  onCancel?: () => void;
  autoFocus?: boolean;
}

export const CommentForm: React.FC<CommentFormProps> = ({
  feedId,
  parentCommentId = null,
  placeholder = "Add a comment...",
  onCancel,
  autoFocus = false,
}) => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const { isCreating } = useAppSelector((state) => state.comments);
  const theme = useTheme();
  const navigate = useNavigate()
  const [content, setContent] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [emojiAnchorEl, setEmojiAnchorEl] = useState<HTMLButtonElement | null>(null);
  const textFieldRef = useRef<HTMLInputElement>(null);

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    
    if (!content.trim() || isCreating) return;

    const commentData = {
      feedId,
      content: content.trim(),
      parentCommentId,
      mentions: [],
    };

    try {
      const result = await dispatch(createComment(commentData));
      
      if (createComment.fulfilled.match(result)) {
        setContent('');
        if (onCancel) {
          onCancel();
        }
      }
    } catch (error) {
      console.error('Failed to create comment:', error);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
    if (e.key === 'Escape' && onCancel) {
      onCancel();
    }
  };

  const handleEmojiClick = useCallback((emojiData: any) => {
    const emoji = emojiData.emoji;
    const input = textFieldRef.current;
    
    if (input) {
      const start = input.selectionStart || 0;
      const end = input.selectionEnd || 0;
      const newContent = content.slice(0, start) + emoji + content.slice(end);
      setContent(newContent);
      
      // Focus back to input and set cursor position after emoji
      setTimeout(() => {
        input.focus();
        input.setSelectionRange(start + emoji.length, start + emoji.length);
      }, 0);
    } else {
      setContent(prev => prev + emoji);
    }
    
    setShowEmojiPicker(false);
  }, [content]);

  const handleEmojiToggle = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (showEmojiPicker) {
      setShowEmojiPicker(false);
      setEmojiAnchorEl(null);
    } else {
      setEmojiAnchorEl(event.currentTarget);
      setShowEmojiPicker(true);
    }
  };

  const handleClickAway = () => {
    setShowEmojiPicker(false);
    setEmojiAnchorEl(null);
  };

  const getAvatarSrc = () => {
    return user?.profilePicture?.url || user?.profilePicture?.url;
  };

  if (!user) return null;

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        p: 1.3,
        '&:focus-within': {
          borderColor: 'primary.main',
        },
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center">
        {user.isLookingForJob ? (
          <Box sx={{ position: 'relative', display: 'inline-block' }} 
           onClick={()=> navigate('/profile/'+user.id)}
          >
            <Box
              sx={{
                position: 'absolute',
                top: -3,
                left: -3,
                right: -3,
                bottom: -3,
                borderRadius: '50%',
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
                padding: '3px',
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  borderRadius: '50%',
                  background: theme.palette.background.paper,
                }}
              />
            </Box>
            <Avatar
              src={getAvatarSrc()}
               onClick={()=> navigate('/profile/'+user.id)}
              sx={{
                width: 32,
                height: 32,
                bgcolor: 'primary.main',
                fontSize: '0.875rem',
                fontWeight: 600,
                position: 'relative',
                zIndex: 1,
              }}
            >
              {user.name?.charAt(0)?.toUpperCase() || 'U'}
            </Avatar>
            <Box
              sx={{
                position: 'absolute',
                bottom: -6,
                left: '50%',
                transform: 'translateX(-50%)',
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
                color: 'white',
                fontSize: '5px',
                fontWeight: 700,
                padding: '1px 4px',
                borderRadius: '6px',
                whiteSpace: 'nowrap',
                zIndex: 2,
                boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
              }}
            >
              OPEN TO WORK
            </Box>
          </Box>
        ) : (
          <Avatar
            src={getAvatarSrc()}
            sx={{
              width: 32,
              height: 32,
              bgcolor: 'primary.main',
              fontSize: '0.875rem',
              fontWeight: 600,
            }}
          >
            {user.name?.charAt(0)?.toUpperCase() || 'U'}
          </Avatar>
        )}
        
        <form onSubmit={handleSubmit} style={{ flex: 1 }}>
          <TextField
            fullWidth
            placeholder={placeholder}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            onKeyDown={handleKeyDown}
            autoFocus={autoFocus}
            variant="standard"
            disabled={isCreating}
            inputRef={textFieldRef}
            InputProps={{
              disableUnderline: true,
              endAdornment: (
                <InputAdornment position="end">
                  <Stack direction="row" spacing={0.5}>
                    <ClickAwayListener onClickAway={handleClickAway}>
                      <Box sx={{ position: 'relative' }}>
                        <IconButton 
                          size="small" 
                          disabled={isCreating}
                          onClick={handleEmojiToggle}
                          sx={{ 
                            color: showEmojiPicker ? 'primary.main' : 'text.secondary',
                            p: 0.5,
                            '&:hover': { 
                              bgcolor: 'action.hover',
                              color: 'primary.main',
                            }
                          }}
                        >
                          <EmojiEmotions fontSize="small" />
                        </IconButton>
                        
                        <Popover
                          open={showEmojiPicker}
                          anchorEl={emojiAnchorEl}
                          onClose={() => setShowEmojiPicker(false)}
                          anchorOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                          }}
                          transformOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                          }}
                          sx={{
                            '& .MuiPopover-paper': {
                              boxShadow: 3,
                              border: '1px solid',
                              borderColor: 'divider',
                            }
                          }}
                        >
                          <EmojiPicker
                            onEmojiClick={handleEmojiClick}
                            autoFocusSearch={false}
                            // theme="light"
                            height={350}
                            width={320}
                            searchDisabled={false}
                            // skinTonePickerLocation="SEARCH"
                            previewConfig={{
                              showPreview: false
                            }}
                          />
                        </Popover>
                      </Box>
                    </ClickAwayListener>
                    
                    <IconButton
                      size="small"
                      onClick={handleSubmit}
                      disabled={!content.trim() || isCreating}
                      sx={{
                        color: content.trim() ? 'primary.main' : 'text.disabled',
                        p: 0.5,
                        '&:hover': {
                          bgcolor: content.trim() ? 'primary.light' : 'action.hover',
                        },
                        '&:disabled': {
                          color: 'text.disabled',
                        },
                      }}
                    >
                      <Send fontSize="small" />
                    </IconButton>
                  </Stack>
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiInputBase-root': {
                fontSize: '0.875rem',
                lineHeight: 1.5,
                color: 'text.primary',
                py: 1,
              },
              '& .MuiInputBase-input': {
                '&::placeholder': {
                  color: 'text.secondary',
                  opacity: 0.8,
                },
              },
            }}
          />
        </form>
      </Stack>
    </Box>
  );
};